import { useLayoutEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { getAnchoredBottomSheetStyle } from './getAnchoredBottomSheetStyle.js'

/**
 * Mobile bottom sheet overlay (planner grocery rail, move-meal, etc.).
 * Portaled to document.body so position:fixed is viewport-relative inside .main-content.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   children: React.ReactNode,
 *   className?: string,
 *   'aria-label'?: string,
 *   anchorRect?: { top: number, bottom: number, left?: number, right?: number, width?: number, height?: number } | null,
 *   anchorPlacement?: 'auto' | 'below',
 * }} props
 */
export default function BottomSheet({
  open,
  onClose,
  children,
  className = '',
  'aria-label': ariaLabel = 'Sheet',
  anchorRect = null,
  anchorPlacement = 'auto',
}) {
  const [viewportTick, setViewportTick] = useState(0)

  useLayoutEffect(() => {
    if (!open || !anchorRect) return undefined
    const onResize = () => setViewportTick((n) => n + 1)
    window.addEventListener('resize', onResize)
    window.addEventListener('orientationchange', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('orientationchange', onResize)
    }
  }, [open, anchorRect])

  const panelStyle = useMemo(() => {
    if (!anchorRect) return undefined
    // viewportTick forces recalc after orientation/resize
    void viewportTick
    return getAnchoredBottomSheetStyle(anchorRect, null, { placement: anchorPlacement }) ?? undefined
  }, [anchorRect, anchorPlacement, viewportTick])

  if (!open) return null
  if (typeof document === 'undefined') return null

  const anchored = Boolean(panelStyle)

  return createPortal(
    <div
      className={[
        'bottom-sheet-overlay',
        anchored ? 'bottom-sheet-overlay--anchored' : '',
      ].filter(Boolean).join(' ')}
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div
        className={[
          'bottom-sheet-panel',
          className,
          anchored ? 'bottom-sheet-panel--anchored' : '',
        ].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bottom-sheet-handle" aria-hidden="true" />
        <button type="button" className="bottom-sheet-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        {children}
      </div>
    </div>,
    document.body,
  )
}
